function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

if (!customElements.get('card-color-swatches')) {
  class CardColorSwatches extends HTMLElement {
    constructor() {
      super();

      this._productCard = null
      this._swatches = null
      this._swatchContainer = null
      this._boundSwatchClickHandler = null
    }

    _swatchClickHandler(event) {
      event.preventDefault();
      event.stopPropagation();
      const selectedSwatch = event.currentTarget;
      this._updateCardProductDetails(selectedSwatch);
      this._updateSelectedSwatch(selectedSwatch);
      this._updateProductImage(selectedSwatch);
    }

    connectedCallback() {
      this._productCard = this.closest('.card');
      this._swatches = Array.from(this.querySelectorAll('.card__color-swatch-item'));
      this._swatchContainer = this.querySelector('.card__color-swatches');
      this._boundSwatchClickHandler = this._swatchClickHandler.bind(this);
      this._init();
    }

    _init() {
      this._swatches.forEach((swatch) => {
        swatch.addEventListener('click', this._boundSwatchClickHandler);
      });
    }

    _updateSelectedSwatch(selectedSwatch) {
      this._swatches.forEach((swatch) => {
        swatch.classList.remove('active');
      });
      selectedSwatch.classList.add('active');
    }

    _updateCardProductDetails(swatch) {
      const swatchTitle = swatch.dataset.productTitle;

      // Update the product title
      const cardHeadings = this._productCard.querySelectorAll('.card__heading a');
      cardHeadings.forEach((heading) => {
        heading.textContent = swatchTitle;
      });

      // Update the product price
      const cardPrices = this._productCard.querySelectorAll('.card-information > .price');
      cardPrices.forEach((price) => {
        price.setAttribute('class', swatch.querySelector('.price').getAttribute('class'));
        price.innerHTML = swatch.querySelector('.price').innerHTML;
      });

      // Update the product link
      const cardLink = this._productCard.querySelectorAll('.card__heading > a');
      cardLink.forEach((link) => {
        link.href = swatch.href;
      });

      // Update badges
      const cardBadges = this._productCard.querySelectorAll('.card__badges');
      cardBadges.forEach((badges) => {
        badges.innerHTML = swatch.querySelector('.card__color-badges').innerHTML;
      });

      // update quick shop link
      const quickAddButton = this._productCard.querySelector('.quick-add__submit');
      if(quickAddButton) quickAddButton.dataset.productUrl = swatch.dataset.productQvUrl;

      // Update Wishlist
      const wishlistButtons = this._productCard.querySelectorAll('.card__wishlist');
      wishlistButtons.forEach((button) => {
        button.setAttribute('data-product-id', swatch.dataset.productId);
        button.setAttribute('data-variant-id', swatch.dataset.variantId);
        button.setAttribute('data-product-url', swatch.dataset.productUrl);
        button.setAttribute('class', swatch.dataset.wishlistClass);
      });

      // refreshes wishlist icon
      if(window._swat) {
        window._swat.initializeActionButtons('#product-grid');
        // send event for separate refresh on other cards, like Nosto
        this._productCard.dispatchEvent(new Event('swym:refresh'));
      }
    }

    _updateProductImage(swatch) {
      const swatchImageSrc = swatch.dataset.imageSrc;
      const productImage = this._productCard.querySelector('.card__media img');

      if (productImage) {
        productImage.src = swatchImageSrc;
        productImage.alt = swatch.dataset.imageAlt || '';
        productImage.removeAttribute('srcset');
      }
    }

    disconnectedCallback() {
      this._swatches.forEach((swatch) => {
        swatch.removeEventListener('click', this._boundSwatchClickHandler);
      });
    }
  }

  customElements.define('card-color-swatches', CardColorSwatches);
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class accordionContent extends HTMLElement {
  constructor() {
    super();
    this.accordionHeading = this.querySelector('.accordion-heading');
    this.accordionHeading.addEventListener('click', this.toggleContent.bind(this));
  }
  toggleContent(e) {
    if (window.innerWidth > 1024) return;
    this.accordionContent = this.querySelector('.accordion-content-wrapper');
    if (!this.accordionContent) return;
    this.accordionHeading.classList.toggle('accordion__active');
    this.accordionContent.style.maxHeight
      ? (this.accordionContent.style.maxHeight = null)
      : (this.accordionContent.style.maxHeight = this.accordionContent.scrollHeight + 'px');
  }
}

customElements.define('accordion-content', accordionContent);


window.theme = window.theme || {};
window.theme.cartRefresh = function() {
  const request = new XMLHttpRequest();
  request.addEventListener('load', handleResponse);
  request.open('GET', '/?sections=cart-drawer,cart-icon-bubble,cart-icon-bubble-sticky', true);
  request.send();

  function handleResponse() {
    const data = JSON.parse(this.responseText);

    function updateElement(tempHTML, selector, replaceSelf) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = tempHTML;
      const target = document.querySelector(selector);
      const source = tempDiv.querySelector(selector);
      if (target && source) {
        replaceSelf ? target.replaceWith(source) : target.innerHTML = source.innerHTML;
      }

      // required for product form on the product page so that it gets the newly fetched cart-drawer
      if (source?.tagName === 'CART-DRAWER') {
        const productForm = document.querySelector('product-form');
        if (productForm) productForm.cart = source;
      }

    }

    updateElement(data['cart-drawer'], 'cart-drawer', true);
    updateElement(data['cart-icon-bubble'], '#cart-icon-bubble', false);
    updateElement(data['cart-icon-bubble-sticky'], '#cart-icon-bubble-sticky', false);

    //open drawer
    document.querySelector('cart-drawer')?.open();

  }
}
